import { ProductMetaData } from '../../../../models/Product';
import { PRODUCT_CLASS_CAMPAIGN } from '../../../store/constants';
import { useMemo } from 'react';
import { getCampaignDaysLeft } from '../../product/ProductCampaignEndDate';
import { useTranslation } from 'react-i18next';

type UseCampaignProduct = {
    /**
     * Indicates whether the given product is a campaign product.
     */
    readonly isCampaignProduct: boolean;

    /**
     * Indicates whether the sale of the campaign product ends soon.
     */
    readonly saleEndsSoon: boolean;

    /**
     * If the sale of the campaign product ends soon, this label contains a short text to inform the user on the
     * remaining time.
     */
    readonly saleEndsSoonLabel: string | undefined;
};

/**
 * Provides general information on campaign products.
 */
export function useCampaignProduct(product: ProductMetaData): UseCampaignProduct {
    const { t } = useTranslation();
    const [saleEndsSoonLabel, daysLeftInCampaign] = useMemo(() => {
        const salesEndDate = product.campaignDetails?.salesEndDate;

        if (salesEndDate) {
            return getCampaignDaysLeft(salesEndDate, t) as [string, number];
        } else {
            return [undefined, undefined];
        }
    }, [product.campaignDetails, t]);

    return {
        isCampaignProduct: product.productClass === PRODUCT_CLASS_CAMPAIGN,
        saleEndsSoon: !!daysLeftInCampaign && daysLeftInCampaign <= 10,
        saleEndsSoonLabel
    };
}
